import styled from 'styled-components';
import type { PrcVersionPayload, PrcVersionSummary } from '@shared/api';
import { IconChevronDown } from '@shared/ui/icons';
import { FleetChip, fleetLeading, fleetType, formatCount, typo } from '@entities/fleet';
import {
  approvalBlockedForAuthor,
  formatIsoDate,
  prcSourceLabel,
  prcStatusLabel,
  prcStatusTone,
} from '@entities/prc';
import { Notice } from '@widgets/FleetRegistry/ui';
import type { PrcAccess } from '../access';
import { ComparisonReport } from '../ComparisonReport';
import { MODELS, count, formatDay, personName } from '../format';
import { Stack } from '../ui';
import { VersionActions } from './VersionActions';
import { VersionObjects } from './VersionObjects';
import { VersionRoute } from './VersionRoute';

const Head = styled.header`
  padding-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Eyebrow = styled.p`
  margin: 0;
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  margin-top: ${({ theme }) => theme.spacing(1)};

  & > h2 {
    margin: 0;
    min-width: 0;
    font-size: ${fleetType.title};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    letter-spacing: -0.01em;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
`;

const Meta = styled.p`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
  text-wrap: pretty;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const NoticeStack = styled(Stack)`
  margin: ${({ theme }) => `${theme.spacing(4)} 0 0`};
`;

const Report = styled.details`
  margin-top: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  & > summary {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
    font-size: ${fleetType.body};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    cursor: pointer;
    list-style: none;
    border-radius: ${({ theme }) => theme.radius.md};
  }

  & > summary::-webkit-details-marker {
    display: none;
  }

  & > summary > .sub {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > summary > svg {
    flex: none;
    margin-left: auto;
    transition: transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  }

  &[open] > summary > svg {
    transform: rotate(180deg);
  }

  & > .body {
    padding: ${({ theme }) => `0 ${theme.spacing(4)} ${theme.spacing(4)}`};
  }
`;

function reportSummary(payload: PrcVersionPayload): string {
  const c = payload.version.comparison;
  if (!c) return '';
  if (!c.recognized) return 'сверки с Excel нет';
  return c.mismatches_total === 0
    ? `сошлось с Excel до копейки: ${formatCount(c.models_ok)} из ${formatCount(c.models)}`
    : `сошлось ${formatCount(c.models_ok)} из ${formatCount(c.models)}, расхождений ${formatCount(c.mismatches_total)}`;
}

/** Была ли последняя развязка — возврат (причина ещё актуальна для доработки). */
function returnedLast(v: PrcVersionPayload['version']): boolean {
  return (
    v.rejected_at !== null &&
    v.decision_comment !== null &&
    (v.submitted_at === null || v.rejected_at >= v.submitted_at)
  );
}

export function VersionHeader({
  payload,
  versions,
  access,
  dirty,
  onCopy,
  onCompare,
  onReread,
}: {
  payload: PrcVersionPayload;
  versions: readonly PrcVersionSummary[];
  access: PrcAccess;
  dirty: number;
  onCopy: () => void;
  onCompare: () => void;
  onReread: () => void;
}) {
  const v = payload.version;
  const years = v.params.years;
  const horizon =
    years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : String(v.params.base_year);
  const base = v.base_version_id ? versions.find((x) => x.id === v.base_version_id) : undefined;
  const source =
    v.source === 'copy' && base
      ? `${prcSourceLabel(v.source)} «${base.label}»`
      : v.source === 'import' && v.comparison?.filename
        ? `${prcSourceLabel(v.source)} «${v.comparison.filename}»`
        : prcSourceLabel(v.source);
  const returned = (v.status === 'rejected' || v.status === 'draft') && returnedLast(v);

  return (
    <Head>
      <Eyebrow>Версия ПРЦ · {source}</Eyebrow>
      <TitleRow>
        <h2>{v.label}</h2>
        <FleetChip $tone={prcStatusTone(v.status)}>{prcStatusLabel(v.status)}</FleetChip>
      </TitleRow>
      <VersionObjects
        key={v.id}
        version={v}
        canWrite={access.canWrite}
        variants={payload.variants}
      />
      <Meta>
        Базовый год <strong>{v.params.base_year}</strong> · горизонт {horizon} ·{' '}
        {count(payload.summary.models, MODELS)}
        {payload.summary.errors > 0 &&
          ` · с ошибкой расчёта ${formatCount(payload.summary.errors)}`}
        {' · '}к утверждению {formatCount(payload.summary.selected)}
      </Meta>
      {v.comment && <Meta>{typo(`Комментарий: ${v.comment}`)}</Meta>}

      <VersionRoute version={v} />

      <NoticeStack>
        {returned && (
          <Notice $tone="warning" role="note">
            <span className="text">
              <strong>
                {typo(
                  `Возвращена на доработку ${formatDay(v.rejected_at)} (${personName(v.rejected_by_name)}):`,
                )}
              </strong>{' '}
              «{v.decision_comment}».
              {access.canWrite && ` ${typo('Поправьте и отправьте на утверждение снова.')}`}
            </span>
          </Notice>
        )}
        {/* Автору-утверждающему причину объясняет подпись у недоступной «Утвердить». */}
        {v.status === 'submitted' &&
          !(access.isApprover && approvalBlockedForAuthor(v, access.userId, access.approveOwn)) && (
            <Notice $tone="info" role="note">
              <span className="text">
                {typo(
                  access.isApprover
                    ? 'Версия ждёт решения: утвердите с датой «действует с» или верните на доработку с причиной.'
                    : 'Версия на утверждении — пока решения нет, её не правят. Утверждают главный экономист, генеральный директор или руководитель.',
                )}
              </span>
            </Notice>
          )}
        {v.status === 'approved' && (
          <Notice $tone="success" role="note">
            <span className="text">
              {typo(
                `ПРЦ отмеченных моделей записана в справочник техники и действует с ${formatIsoDate(v.valid_from)}${v.objects.length > 0 ? ` на ${v.objects.length === 1 ? 'объекте' : 'объектах'} ${v.objects.map((o) => `«${o.name}»`).join(', ')}` : ' на объектах без своей ПРЦ'}${(payload.variants ?? []).length > 0 ? `; по вариантам условий — на их объектах: ${(payload.variants ?? []).map((x) => `«${x.name}» — ${x.objects.map((o) => `«${o.name}»`).join(', ')}`).join('; ')}` : ''}. Утверждённую версию не меняют — для новых цен создайте копию.`,
              )}
              {v.decision_comment &&
                ` ${typo(`Комментарий при утверждении: «${v.decision_comment}».`)}`}
            </span>
          </Notice>
        )}
        {v.status === 'superseded' && (
          <Notice $tone="info" role="note">
            <span className="text">
              {typo(
                'Версия заменена: ни одна её цена больше не действует, в справочнике техники — цены новых версий. Это история.',
              )}
            </span>
          </Notice>
        )}
      </NoticeStack>

      <VersionActions
        payload={payload}
        access={access}
        dirty={dirty}
        onCopy={onCopy}
        onCompare={onCompare}
        onReread={onReread}
      />

      {v.comparison && (
        <Report>
          <summary>
            Отчёт загрузки книги <span className="sub">· {reportSummary(payload)}</span>
            <IconChevronDown size={16} />
          </summary>
          <div className="body">
            <ComparisonReport comparison={v.comparison} />
          </div>
        </Report>
      )}
    </Head>
  );
}
